import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { TournamentService } from "services/tournaments/tournament.service"

import { PointRule, RulePreset } from "types/tournament.types"

import { PointRulesToolbar } from "./PointRulesToolbar"
import { RuleEditor } from "./RuleEditor"
import { SavePresetModal } from "./SavePresetModal"

interface Props {
  value: PointRule[]
  onChange: (value: PointRule[]) => void
}

export const PointRulesBuilder: FC<Props> = ({ value, onChange }) => {
  const [rules, setRules] = useState<PointRule[]>(value.length > 0 ? value : [])
  const [presets, setPresets] = useState<RulePreset[]>([])
  const [showPresetModal, setShowPresetModal] = useState(false)

  useEffect(() => {
    loadPresets()
  }, [])

  const loadPresets = async () => {
    try {
      const result = await TournamentService.listPresets()
      if (Array.isArray(result)) {
        setPresets(result)
      }
    } catch {
      // silent
    }
  }

  const addRule = () => {
    const newRule: PointRule = {
      id: `rule-${Date.now()}`,
      name: `Rule ${rules.length + 1}`,
      enabled: true,
      if: { type: "always" },
      then: { type: "award_bet_percentage", percentage: 10 },
      modifier: { type: "no_limit" }
    }
    const newRules = [...rules, newRule]
    setRules(newRules)
    onChange(newRules)
  }

  const removeRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index)
    setRules(newRules)
    onChange(newRules)
  }

  const updateRule = (index: number, updatedRule: PointRule) => {
    const newRules = [...rules]
    newRules[index] = updatedRule
    setRules(newRules)
    onChange(newRules)
  }

  const toggleRule = (index: number) => {
    const newRules = [...rules]
    newRules[index] = { ...newRules[index], enabled: !newRules[index].enabled }
    setRules(newRules)
    onChange(newRules)
  }

  const loadFromPreset = (preset: RulePreset) => {
    const loadedRules = preset.rules.map((r: PointRule) => ({
      ...r,
      enabled: r.enabled !== false
    }))
    setRules(loadedRules)
    onChange(loadedRules)
    toast.success(`Loaded preset: ${preset.name}`)
  }

  return (
    <div className="space-y-4">
      <PointRulesToolbar
        presets={presets}
        hasRules={rules.length > 0}
        onAddRule={addRule}
        onLoadPreset={loadFromPreset}
        onSavePreset={() => setShowPresetModal(true)}
      />

      {rules.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">No point rules configured</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {presets.length > 0 ? "Load a preset or add rules manually" : "Add rules to configure scoring"}
          </p>
          <div className="flex justify-center gap-3 mt-3">
            <button type="button" onClick={addRule} className="text-[rgba(169,78,206,1)] hover:text-[rgba(169,78,206,0.8)]">
              Add your first rule
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule, index) => (
            <RuleEditor
              key={rule.id}
              rule={rule}
              onUpdate={(updated) => updateRule(index, updated)}
              onRemove={() => removeRule(index)}
              onToggle={() => toggleRule(index)}
            />
          ))}
        </div>
      )}

      <SavePresetModal
        open={showPresetModal}
        rules={rules}
        onClose={() => setShowPresetModal(false)}
        onSaved={() => {
          setShowPresetModal(false)
          loadPresets()
        }}
      />
    </div>
  )
}
